import {
  Application,
  ApplicationName,
  formatApplication,
  isAccountService,
  LightWeightApplication,
  lightWeightApplicationSchema
} from "../applications/Application";
import {formatRole, Role, RoleName, RoleType, StandardRole} from "./Role";
import {User} from "./User";
import {isAdmin, isAdminForApplication, isApplicationAdmin, isCompanyAdmin} from "./AccountServiceRoles";
import {mixed, object, ObjectSchema, string} from "yup";
import {TFunction} from "i18next";
import {formError} from "../../shared/components/forms/formUtils";

export interface ApplicationRole {
  id?: string | number,
  application: LightWeightApplication,
  role: RoleName
}

export function applicationRoleSchema(t: TFunction): ObjectSchema<ApplicationRole> {
  return object({
    id: string(),
    application: lightWeightApplicationSchema.defined(fieldDefinedError(t)),
    role: mixed<RoleName>().defined(fieldDefinedError(t)),
  });
}

function fieldDefinedError(t: TFunction) {
  return formError(t, 'mustBeDefined', 'User.');
}

export function formatApplicationRole(application: Application, roleName: RoleName) {
  const role = application.roles.find(curr => curr.name === roleName);
  if (!role) {
    return roleName
  }
  return formatApplication(application) + ': ' + formatRole(application, roleName)
}

export function formatApplicationRoleHint(application: Application, role: Role, t: TFunction) {
  if (application.name === 'ACCOUNT_SERVICE' && role.name === StandardRole.ADMIN) {
    return t('ApplicationRoles.infoAccountServiceAdminRole');
  } else if (application.name === 'ACCOUNT_SERVICE' && role.name === StandardRole.COMPANY_ADMIN) {
    return t('ApplicationRoles.infoCompanyAdminRole');
  } else if (application.name !== 'ACCOUNT_SERVICE' && role.name === application.adminRole) {
    return t('ApplicationRoles.infoApplicationAdminRole');
  } else {
    return undefined;
  }
}

/**
 * We do not show application admin and account service user roles
 * - Normal users my not see any other roles
 * - Company Admins may only see USER and COMPANY_ADMIN
 * - Application Admins may only see APPLICATION_ADMIN and USER for their applications and COMPANY_ADMIN in Account Service
 */
export function mayShowRoleInForm(account: User, application: Application, role: Role, applicationRole: ApplicationRole): boolean {
  return role.name === applicationRole.role || // We always show the current role of the user in the application
    (mayConfigureRole(account, application, role) && !isStandardAccountServiceRole(application, role.name))
}

function mayConfigureRole(account: User, application: Application, role: Role): boolean {
  if (isAdmin(account)) {
    return true
  } else if (isCompanyAdmin(account)) {
    return (role.type !== RoleType.RESTRICTED) && (role.name !== application.adminRole)
  } else if (isApplicationAdmin(account)) {
    return isAdminForApplication(account, application) || (isAccountService(application) && role.name === StandardRole.COMPANY_ADMIN)
  } else {
    return false
  }
}

/**
 * We do not show application admin and account service user roles
 */
export function isStandardAccountServiceRole(application: Application | LightWeightApplication, role: RoleName): boolean {
  return role !== StandardRole.ADMIN
    && role !== StandardRole.COMPANY_ADMIN
    && isAccountService(application)
}

export function filterAccountServiceRoles(applicationRoles: ApplicationRole[]): ApplicationRole[] {
  // We do not show account service users and application admins
  return applicationRoles.filter(applicationRole => !isStandardAccountServiceRole(applicationRole.application, applicationRole.role))
}

/**
 * Returns the application roles of this user in an easy structure. It also filters out application roles that should not be visible.
 */
export function getVisibleApplicationRoles(user: User): {applicationName: ApplicationName, role: RoleName}[] {
  const applicationRoles = filterAccountServiceRoles(user.applicationRoles);
  return applicationRoles.map(applicationRole => {
    return {
      applicationName: applicationRole.application.name, role: applicationRole.role
    }
  })
}
